import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, PLATFORM_ID, inject, runInInjectionContext, signal } from '@angular/core';
import {
  STAFF_HTTP_API_BASE_URL,
  currentReturnPath,
} from '@sbe-spinoff-client/staff-http';
import { firstValueFrom } from 'rxjs';
import { STAFF_OIDC_AUTH_CONFIG } from './staff-oidc-auth.config';
import {
  buildAuthorizeRedirectUrl,
  resolveReturnPath,
} from './staff-oidc-auth-url.util';

/** 탭 사이 refresh 직렬화에 쓰는 Web Locks 이름 — 같은 origin 의 모든 탭이 공유한다. */
const REFRESH_LOCK_NAME = 'staff-refresh';

interface StaffRefreshResponse {
  accessToken: string;
  accessExpiresInSeconds?: number;
}

/**
 * staff-web 세션 상태 — 사용자 웹과 같은 스핀오프 세션(refresh 쿠키 + 메모리 access token).
 *
 * 흐름:
 *   1. `redirectToSignIn()` → `<baseUrl>/staff/auth/sso/<provider>/authorize-redirect` 이동.
 *   2. OP 인증 완료 → 콜백이 HttpOnly refresh 쿠키(`spinoff-staff-refresh`, path
 *      `/staff/auth`)를 심고 SPA 로 복귀한다. 이 시점엔 access token 이 없다.
 *   3. `loadMe()` — 토큰이 없으면 `POST /staff/auth/refresh` 로 복원한 뒤
 *      `GET /staff/auth/me`(Bearer)로 확인한다. 응답 자체를 "인증 완료"로 볼지는
 *      `provideStaffOidcAuth({ isAuthorized })`로 앱이 결정한다.
 *   4. API 요청: `staffBearerInterceptor`가 access token 을 붙이고, 401 이면 refresh 를
 *      1회 시도해 재전송한다.
 *   5. `logout()` → `POST /staff/auth/signout`.
 *
 * access token 은 JS 메모리(signal)에만 둔다 — 새로고침하면 refresh 쿠키로 복원한다.
 */
@Injectable({ providedIn: 'root' })
export class StaffOidcAuthService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(STAFF_HTTP_API_BASE_URL);
  private readonly config = inject(STAFF_OIDC_AUTH_CONFIG);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly injector = inject(Injector);

  private readonly _me = signal<unknown>(null);
  private readonly _accessToken = signal<string | null>(null);
  private readonly _sessionChecked = signal(false);
  private refreshInFlight: Promise<boolean> | null = null;

  /** 최초 세션 확인 완료 여부 — 성공·실패 무관하게 loadMe() 첫 호출 후 true */
  readonly sessionChecked = this._sessionChecked.asReadonly();
  /** 캐싱된 `me` 응답 원본 — 구체 타입은 앱이 `isAuthorized`에서 좁혀 쓴다. */
  readonly me = this._me.asReadonly();
  /** 현재 메모리의 access token — `staffBearerInterceptor` 가 읽는다. */
  readonly accessToken = this._accessToken.asReadonly();

  /**
   * 앱이 자체 로그인으로 받은 access token 을 이 서비스에 넘긴다 — 이후 요청의 Bearer, 401 시 refresh, 로그아웃(`signout`)을 이 서비스가 맡는다.
   * refresh 토큰은 로그인 응답이 심은 HttpOnly 쿠키라 SPA 가 다루지 않는다. 세션 확인(`sessionChecked`)도 끝난 것으로 본다.
   */
  adoptSession(accessToken: string): void {
    this._accessToken.set(accessToken);
    this._sessionChecked.set(true);
  }

  /** 이 요청의 401 을 호출부가 스스로 처리하는지 — `provideStaffOidcAuth({ handlesUnauthorized })`. */
  handlesUnauthorized(url: string): boolean {
    return this.config.handlesUnauthorized(url);
  }

  /** guard 가 세션 확인 시 호출. 인증 완료면 true, 아니면 false. */
  async loadMe(): Promise<boolean> {
    try {
      if (!this._accessToken() && !(await this.refreshAccessToken())) {
        this._me.set(null);
        return false;
      }
      const me = await firstValueFrom(this.http.get('/staff/auth/me'));
      this._me.set(me);
      return this.config.isAuthorized(me);
    } catch {
      this._me.set(null);
      return false;
    } finally {
      this._sessionChecked.set(true);
    }
  }

  /**
   * refresh 쿠키로 access token 을 새로 받는다. 동시에 여러 번 불려도 요청 하나로 합친다
   * (refresh 는 쿠키를 회전하므로 병렬 호출하면 한쪽이 무효 쿠키로 401 을 받는다).
   * 쿠키가 없거나 무효(401)·네트워크 오류면 false 이고 메모리 토큰은 비운다.
   */
  refreshAccessToken(): Promise<boolean> {
    this.refreshInFlight ??= this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  /**
   * 탭 사이에서도 refresh 를 한 번에 하나만 보낸다. refresh 쿠키는 1회용 회전이고 쿠키 저장소는 탭끼리 공유되므로, 두 탭이 같은 쿠키로 동시에
   * 보내면 늦게 도착한 쪽이 폐기된 쿠키로 401 을 받아 로그인 서버로 튕긴다(브라우저 재시작 뒤 탭 여러 개가 함께 복원될 때).
   * 락을 잡으면 다음 탭은 앞 탭의 응답(= 새 쿠키 저장) 뒤에 보내므로 회전된 새 쿠키를 쓴다. 락 API 가 없는 환경은 직렬화 없이 보낸다.
   */
  private async doRefresh(): Promise<boolean> {
    const locks = this.isBrowser && typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks) return this.requestRefresh();
    try {
      return await locks.request(REFRESH_LOCK_NAME, () => this.requestRefresh());
    } catch {
      // 락을 못 잡는 환경(opaque origin 의 sandboxed iframe 등 `SecurityError`) — 직렬화만 포기하고 refresh 는 보낸다.
      // `requestRefresh` 는 예외를 던지지 않으므로(실패는 false) 이 catch 는 락 획득 실패만 잡는다.
      return this.requestRefresh();
    }
  }

  private async requestRefresh(): Promise<boolean> {
    try {
      const res = await firstValueFrom(
        this.http.post<StaffRefreshResponse>('/staff/auth/refresh', null, {
          withCredentials: true,
        }),
      );
      this._accessToken.set(res.accessToken);
      return true;
    } catch {
      this._accessToken.set(null);
      return false;
    }
  }

  /**
   * staff-api → OP staff authorize-redirect 로 이동. `window` 가 없는 SSR 렌더 중에
   * guard 가 이 메서드를 호출하면(미인증 응답) `window.location.assign`이 서버 프로세스를
   * 죽이므로 브라우저에서만 실행한다(`staff-http`의 `provideStaffHttpApiBaseUrl`과 동일한
   * SSR 가드 원칙 — 이 lib을 쓰는 앱은 현재 SSR 이 아니지만 향후 도입 대비).
   */
  redirectToSignIn(attemptedPath?: string): void {
    if (!this.isBrowser) return;
    const returnPath = resolveReturnPath(
      attemptedPath ?? currentReturnPath(),
      this.config.returnPathFallback,
    );
    // 앱이 자체 로그인 화면을 가졌으면 SSO 이동 대신 그쪽으로 보낸다.
    if (this.config.signIn) {
      // 주입 컨텍스트에서 부른다 — 앱의 `signIn` 이 `inject(Router)` 같은 것을 쓸 수 있게.
      runInInjectionContext(this.injector, () => this.config.signIn?.(returnPath));
      return;
    }
    window.location.assign(
      buildAuthorizeRedirectUrl(this.baseUrl, returnPath, this.config.provider),
    );
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post('/staff/auth/signout', null, {
          withCredentials: true,
        }),
      );
    } catch {
      // 멱등 — 쿠키만 폐기되어도 정상.
    }
    this._accessToken.set(null);
    this._me.set(null);
    // sessionChecked는 되돌리지 않는다 — sms는 로그아웃 후 window.location.href로 풀
    // 페이지 이동해 서비스 인스턴스 자체가 소멸되니 무관하지만, commerce는 router.navigateByUrl()
    // (SPA 내부 이동)이라 App 인스턴스가 살아있다. commerce의 showBootLoading은
    // `isProtectedEntry && !sessionChecked()`로 부팅 오버레이를 게이팅하는데, 여기서
    // false로 되돌리면 로그아웃 직후 오버레이가 재노출되고 `/signed-out`엔 guard가
    // 없어 sessionChecked가 다시 true로 안 돌아와 영구히 멈춘다(코드 리뷰로 발견).
  }
}
